<script setup lang="ts">
// K_t tracking dashboard Task 4: top-level 'kt' tab scoreboard. Consumes
// api.getKtDailyScoreboard() (Task 3) — one row per fund (bga/jh/gge/gold),
// each carrying corr/rmseBps/n against the gate thresholds computed
// server-side. The load-bearing feature is the RED-below-gate tile: a fund
// that is `available` but fails its gate (`gatePass === false`) renders
// with the `tile--fail` class so an operator scanning the tab spots a
// tracking regression at a glance, without reading numbers. An
// `available === false` fund (no rows yet / fetch failure upstream) is a
// separate, less alarming "warn" state — not a gate failure.
import { ref, onMounted, onUnmounted } from 'vue'
import { api, type KtDailyScoreboard } from '../api/client'
import StepAsideAnchorTile from './StepAsideAnchorTile.vue'

const data = ref<KtDailyScoreboard | null>(null)
const loading = ref(true)
const error = ref<string | null>(null)
const legendOpen = ref(false)
let timer: ReturnType<typeof setInterval> | null = null

async function fetchData() {
  try {
    const result = await api.getKtDailyScoreboard()
    data.value = result
    error.value = null
  } catch (e) {
    error.value = e instanceof Error ? e.message : 'Failed to load K_t scoreboard'
  } finally {
    loading.value = false
  }
}

function retry() {
  loading.value = true
  fetchData()
}

function fmtCorr(v: number | null): string {
  return v !== null && v !== undefined ? v.toFixed(2) : '—'
}

function fmtRmse(v: number | null): string {
  return v !== null && v !== undefined ? v.toFixed(1) : '—'
}

function fmtRatio(v: number | null): string {
  return v !== null && v !== undefined ? v.toFixed(2) : '—'
}

// Vol-scaled tracking-ratio RAG thresholds (vol-scaled-gate-design.md §2,
// 2026-10-04 live calibration: cross-fund history clusters 0.20-0.46).
// HARD RULE: this dot is informational only — it must NEVER be promoted
// into `gatePass` / `tileClass` below. The tile's fail border stays driven
// by `gatePass` (corr + RMSE) alone, regardless of this colour.
function ratioDotClass(v: number | null): string | null {
  if (v === null || v === undefined) return null
  if (v >= 0.50) return 'ratio-dot--red'
  if (v >= 0.40) return 'ratio-dot--amber'
  return 'ratio-dot--green'
}

function tileClass(fund: KtDailyScoreboard['funds'][number]): string {
  if (!fund.available) return 'tile--warn'
  if (fund.gatePass === false) return 'tile--fail'
  return 'tile--pass'
}

// STRIKE-ADJ (i): per-tile worked example of the fair-value term with the
// latest session's real prints (server-side KtStrikeAdjExplainer).
const adjOpen = ref<Record<string, boolean>>({})
function toggleAdj(fund: string) {
  adjOpen.value = { ...adjOpen.value, [fund]: !adjOpen.value[fund] }
}

function fmtBps(v: number | null | undefined): string {
  if (v === null || v === undefined) return '—'
  return `${v >= 0 ? '+' : ''}${v.toFixed(1)}`
}

// g(x) = x when |x| > trigger, else 0 — mirrors EtfTrickDailyService.GateByTrigger.
function gated(v: number | null | undefined, triggerBps: number): number | null {
  if (v === null || v === undefined) return null
  return Math.abs(v) > triggerBps ? v : 0
}

function fmtBeta(b: number): string {
  return Number.isInteger(b * 100) ? b.toFixed(2) : String(b)
}

onMounted(() => {
  fetchData()
  timer = setInterval(fetchData, 60000)
})

onUnmounted(() => {
  if (timer !== null) clearInterval(timer)
})
</script>

<template>
  <div class="kt-tracking-panel" data-testid="kt-tracking-panel">
    <div v-if="loading && !data" class="kt-loading">Loading…</div>

    <div v-if="error" class="kt-error-banner">
      <span>{{ error }}</span>
      <button class="kt-retry-btn" @click="retry">Retry</button>
    </div>

    <template v-if="data">
      <div class="kt-gate-header">
        <span>Gate: corr &ge; {{ data.gate.minCorr }} &middot; RMSE &le; {{ data.gate.maxRmseBps }} bps</span>
        <button
          type="button"
          class="kt-info-btn"
          :aria-expanded="legendOpen"
          aria-controls="kt-metrics-legend"
          :aria-label="legendOpen ? 'Hide metrics explanation' : 'Show metrics explanation'"
          data-testid="kt-info-toggle"
          @click="legendOpen = !legendOpen"
        >i</button>
      </div>
      <div class="kt-caveat">
        RMSE magnitude scales with each fund's return volatility — compare within-fund over time; use the ratio below for a cross-fund comparison.
      </div>

      <section
        v-if="legendOpen"
        id="kt-metrics-legend"
        class="kt-legend"
        role="region"
        aria-label="K_t tracking metrics explanation"
        data-testid="kt-metrics-legend"
      >
        <p class="kt-legend-lead">
          Each tile compares one fund's daily <b>K_t</b> basket return against its
          <b>official NAV</b> daily return, over a trailing sample of aligned trading sessions.
          &ldquo;Aligned&rdquo; = an official print with a K_t row on the same date. When the fund skips a
          dealing day the basket still trades (Gold on US holidays; UK/US mismatches elsewhere), the
          print-to-print official return is compared with K_t <b>compounded</b> over the same span, so
          the session counts once and like is compared with like. Spans over 7 calendar days (scraper
          outage) and prints with no K_t row are excluded, not zero-filled.
        </p>

        <dl class="kt-legend-list">
          <dt>corr — Pearson information coefficient</dt>
          <dd>
            Correlation between K_t daily return and official NAV daily return over the window's
            aligned pairs.
            <code>corr = Σ(dx·dy) / √(Σdx² · Σdy²)</code>
            where <code>dx = kt<sub>t</sub> − mean(kt)</code> and
            <code>dy = official<sub>t</sub> − mean(official)</code>. Range −1 to +1;
            higher = better direction-following.
            <b>Gate: corr &ge; {{ data.gate.minCorr }}</b>.
          </dd>

          <dt>RMSE bps — root-mean-square return error</dt>
          <dd>
            <code>RMSE_bps = √(mean((kt − official)²)) × 10 000</code>, on raw fractional
            daily-return differences, scaled to basis points at the end.
            Note this is <b>not</b> volatility-normalised: a fund whose daily returns swing more
            will show a larger RMSE for the same relative tracking quality.
            <b>Gate: RMSE &le; {{ data.gate.maxRmseBps }} bps</b>.
          </dd>

          <dt>ratio — vol-scaled tracking ratio</dt>
          <dd>
            <code>ratio = RMSE &divide; &sigma; of official daily returns (same window)</code> — the
            fraction of a typical daily move the K_t replica misses. &sigma; is the official series'
            own standard deviation over the same aligned window, so the ratio is comparable
            <b>across</b> funds even though RMSE alone is not.
            <b>Green</b> &lt; 0.40 &middot; <b>amber</b> 0.40&ndash;0.50 &middot; <b>red</b> &ge; 0.50.
            Informational only — it never feeds <code>gatePass</code> or any gate, shadow or live.
          </dd>

          <dt>N — sample size</dt>
          <dd>
            Count of aligned pairs actually used to compute <code>corr</code> and <code>RMSE</code>
            within this window. Fewer than 10 aligned pairs → the row renders as
            <b>unavailable</b> (amber) rather than passing or failing the gate.
          </dd>
        </dl>

        <dl class="kt-legend-list">
          <dt>Window</dt>
          <dd>
            Trailing <b>{{ data.windowSessions }}</b> aligned trading sessions
            (default 60; override with <code>?window=N</code> on
            <code>/api/kt/daily-scoreboard</code>). Older days roll out; newer aligned days roll in.
          </dd>

          <dt>How often the numbers change</dt>
          <dd>
            The <code>synthetic_bga_kt</code> writer (<code>EtfTrickDailyService</code>) appends
            one K_t row per fund per session at <b>23:30 UTC</b>. Each fund's official NAV lands
            whenever HL stamps it (usually 15:30–17:30 UK on strike day; occasionally later).
            This panel re-fetches the scoreboard every <b>60 s</b>, but the metrics only
            meaningfully change when the day's new K_t and official pair complete.
          </dd>

          <dt>Colour coding</dt>
          <dd>
            <b>Green</b> — passes both gate conditions.
            <b>Red (BELOW GATE)</b> — available but fails <code>corr &ge; {{ data.gate.minCorr }}</code>
            and/or <code>RMSE &le; {{ data.gate.maxRmseBps }}</code>.
            <b>Amber (unavailable)</b> — fewer than 10 aligned pairs in the window,
            or a data-source read failed (see the tile's reason line).
          </dd>
        </dl>
      </section>

      <div class="kt-tile-grid">
        <div
          v-for="fund in data.funds"
          :key="fund.fund"
          class="tile"
          :class="tileClass(fund)"
          :data-testid="`kt-tile-${fund.fund}`"
        >
          <div class="tile-label">
            {{ fund.label }}
            <span
              v-if="fund.strikeAdjApplied"
              class="tile-tag"
              title="K_t return includes the strike-hour FX and the strike fair-value term (tracking only — never a decision input)"
            >strike-adj</span>
            <button
              v-if="fund.strikeAdjApplied && fund.strikeAdj"
              type="button"
              class="kt-info-btn kt-info-btn--tile"
              :aria-expanded="!!adjOpen[fund.fund]"
              :aria-controls="`kt-strike-adj-${fund.fund}`"
              :aria-label="adjOpen[fund.fund] ? 'Hide strike-adj formula' : 'Show strike-adj formula'"
              :data-testid="`kt-strike-adj-info-${fund.fund}`"
              @click="toggleAdj(fund.fund)"
            >i</button>
          </div>

          <section
            v-if="fund.strikeAdjApplied && fund.strikeAdj && adjOpen[fund.fund]"
            :id="`kt-strike-adj-${fund.fund}`"
            class="kt-legend kt-adj-legend"
            role="region"
            :aria-label="`${fund.label} strike-adj formula`"
            :data-testid="`kt-strike-adj-legend-${fund.fund}`"
          >
            <p class="kt-legend-lead">
              The tile's K_t return is <code>return_bps + strike_adj_bps</code>. The term models the
              fund pricing part of the overnight move into its strike, then unwinding it the next
              session once the close is in the basket. FX leg: {{ fund.strikeAdj.fxNote }}.
              Tracking only — never a decision input.
            </p>
            <dl class="kt-legend-list">
              <dt>Signal</dt>
              <dd>
                <code>m = ln({{ fund.strikeAdj.proxy }}@{{ fund.strikeAdj.clock }} ÷ {{ fund.strikeAdj.proxy }}@16:00 New York, prior weekday)</code>
                from <code>{{ fund.strikeAdj.signalCsv }}</code>, shown in bps.
              </dd>
              <dt>Formula</dt>
              <dd v-if="fund.strikeAdj.form === 'trigger'">
                <code>strike_adj = β × (g(m<sub>T</sub>) − g(m<sub>prev</sub>))</code>,
                <code>g(x) = x if |x| &gt; {{ fund.strikeAdj.triggerBps }} bps, else 0</code>
                — the fund only fair-values moves above the trigger.
                β = <b>{{ fmtBeta(fund.strikeAdj.beta) }}</b>, trigger = <b>{{ fund.strikeAdj.triggerBps }} bps</b>.
              </dd>
              <dd v-else>
                <code>strike_adj = β × (m<sub>T</sub> − m<sub>prev</sub>)</code>,
                β = <b>{{ fmtBeta(fund.strikeAdj.beta) }}</b> (fitted pass-through of the overnight move into the strike NAV).
              </dd>
              <dt>Latest session{{ fund.strikeAdj.latestDate ? ` — ${fund.strikeAdj.latestDate}` : '' }}</dt>
              <dd v-if="fund.strikeAdj.adjBps !== null && fund.strikeAdj.mtBps !== null && fund.strikeAdj.mPrevBps !== null">
                m<sub>T</sub> = <b>{{ fmtBps(fund.strikeAdj.mtBps) }}</b> bps,
                m<sub>prev</sub> ({{ fund.strikeAdj.prevDate }}) = <b>{{ fmtBps(fund.strikeAdj.mPrevBps) }}</b> bps
                <template v-if="fund.strikeAdj.form === 'trigger'">
                  → g(m<sub>T</sub>) = {{ fmtBps(gated(fund.strikeAdj.mtBps, fund.strikeAdj.triggerBps)) }},
                  g(m<sub>prev</sub>) = {{ fmtBps(gated(fund.strikeAdj.mPrevBps, fund.strikeAdj.triggerBps)) }}
                  <br />
                  <code>{{ fmtBeta(fund.strikeAdj.beta) }} × ({{ fmtBps(gated(fund.strikeAdj.mtBps, fund.strikeAdj.triggerBps)) }} − ({{ fmtBps(gated(fund.strikeAdj.mPrevBps, fund.strikeAdj.triggerBps)) }})) = <b>{{ fmtBps(fund.strikeAdj.adjBps) }} bps</b></code>
                </template>
                <template v-else>
                  <br />
                  <code>{{ fmtBeta(fund.strikeAdj.beta) }} × ({{ fmtBps(fund.strikeAdj.mtBps) }} − ({{ fmtBps(fund.strikeAdj.mPrevBps) }})) = <b>{{ fmtBps(fund.strikeAdj.adjBps) }} bps</b></code>
                </template>
                <span v-if="fund.strikeAdj.storedAdjBps !== null && fund.strikeAdj.storedAdjBps !== fund.strikeAdj.adjBps" class="kt-adj-stale">
                  (stored row: {{ fmtBps(fund.strikeAdj.storedAdjBps) }} bps — signal file changed since the write; the next run rewrites it)
                </span>
              </dd>
              <dd v-else>
                No {{ fund.strikeAdj.proxy }} print for this session pair (holiday or early close) — the term is null on such days.
              </dd>
            </dl>
          </section>

          <template v-if="fund.available">
            <div class="tile-row"><span class="tile-metric-label">corr</span><span class="tile-metric-value">{{ fmtCorr(fund.corr) }}</span></div>
            <div class="tile-row"><span class="tile-metric-label">RMSE bps</span><span class="tile-metric-value">{{ fmtRmse(fund.rmseBps) }}</span></div>
            <!-- Vol-scaled tracking ratio (informational-only sub-indicator; see
                 ratioDotClass HARD RULE above) — never feeds tileClass/gatePass. -->
            <div class="tile-row">
              <span class="tile-metric-label">ratio</span>
              <span class="tile-metric-value" :data-testid="`kt-ratio-value-${fund.fund}`">
                {{ fmtRatio(fund.ratio) }}
                <span
                  v-if="fund.ratio !== null && fund.ratio !== undefined"
                  class="ratio-dot"
                  :class="ratioDotClass(fund.ratio)"
                  :data-testid="`kt-ratio-dot-${fund.fund}`"
                  aria-hidden="true"
                ></span>
              </span>
            </div>
            <div class="tile-row"><span class="tile-metric-label">N</span><span class="tile-metric-value">{{ fund.n }}</span></div>
            <div v-if="fund.gatePass === false" class="tile-status">BELOW GATE</div>
          </template>
          <template v-else>
            <div class="tile-reason">{{ fund.reason ?? 'unavailable' }}</div>
          </template>
        </div>
      </div>

      <!-- BGA/JH step-aside anchor durability tile (V107 arc, 2026-09-21).
           Placed BELOW the K_t magnitude scale tiles: a stale anchor
           invalidates the step-aside decision this pane's numbers feed. -->
      <StepAsideAnchorTile class="kt-tracking-panel__anchor" />
    </template>
  </div>
</template>

<style scoped>
.kt-tracking-panel {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  padding: 0.75rem;
}

.kt-loading {
  color: #888;
  font-size: 0.9rem;
}

.kt-error-banner {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 8px 10px;
  background: #3a1b1b;
  border: 1px solid #f44336;
  border-radius: 4px;
  color: #f44336;
  font-size: 0.85rem;
}

.kt-retry-btn {
  background: transparent;
  color: #f44336;
  border: 1px solid #f44336;
  border-radius: 4px;
  padding: 4px 10px;
  font-size: 0.8rem;
  cursor: pointer;
  min-height: 32px;
}
.kt-retry-btn:hover { background: #f4433622; }

.kt-gate-header {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.85rem;
  color: #ccc;
  font-weight: 600;
}

.kt-info-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  padding: 0;
  border-radius: 50%;
  background: transparent;
  color: #888;
  border: 1px solid #555;
  font-family: 'Times New Roman', Times, serif;
  font-style: italic;
  font-weight: 700;
  font-size: 0.75rem;
  line-height: 1;
  cursor: pointer;
  min-height: 20px; /* deliberately override the 32px touch minimum — inline glyph */
}
.kt-info-btn:hover,
.kt-info-btn[aria-expanded="true"] {
  color: #e6edf3;
  border-color: #999;
  background: #ffffff10;
}
.kt-info-btn:focus-visible {
  outline: 2px solid #58a6ff;
  outline-offset: 2px;
}

.kt-legend {
  background: #141414;
  border: 1px solid #2a2a2a;
  border-radius: 6px;
  padding: 0.75rem 0.9rem;
  font-size: 0.8rem;
  color: #ccc;
  line-height: 1.5;
}
.kt-legend-lead { margin: 0 0 0.65rem 0; color: #b3b3b3; }
.kt-legend-list { margin: 0.4rem 0 0.2rem 0; }
.kt-legend-list dt {
  color: #e6edf3;
  font-weight: 600;
  margin-top: 0.55rem;
}
.kt-legend-list dt:first-of-type { margin-top: 0; }
.kt-legend-list dd { margin: 0.15rem 0 0 0; color: #b3b3b3; }
.kt-legend code {
  font-family: 'JetBrains Mono', monospace;
  font-size: 0.78rem;
  color: #d4b57a;
  background: #ffffff08;
  padding: 0 0.25rem;
  border-radius: 3px;
}
.kt-legend b { color: #e6edf3; }

.kt-caveat {
  font-size: 0.75rem;
  color: #888;
  font-style: italic;
}

.kt-tile-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 0.75rem;
  /* 2026-09-19 (paolo): with the default `stretch`, opening one tile's
     strike-adj (i) legend stretched every tile in the row to the same
     height, so the neighbours looked expanded too. Each tile keeps its own
     height now — only the clicked (i) grows its tile. */
  align-items: start;
}

.tile {
  background: #1a1a1a;
  border: 1px solid #333;
  border-left: 3px solid #333;
  border-radius: 6px;
  padding: 0.75rem;
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
}

.tile--pass { border-left-color: #4caf50; }

.tile--fail {
  border-left-color: #f44336;
  background: #2a1414;
  border-color: #5a2a2a;
}

.tile--warn {
  border-left-color: #fbc02d;
  background: #1a1a1a;
  opacity: 0.85;
}

.tile-label {
  font-size: 0.8rem;
  color: #aaa;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.tile-row {
  display: flex;
  justify-content: space-between;
  gap: 0.5rem;
  font-family: 'JetBrains Mono', monospace;
  font-size: 0.85rem;
}

.tile-metric-label { color: #888; }
.tile-metric-value { color: #ccc; display: inline-flex; align-items: center; gap: 0.35rem; }

.ratio-dot {
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
}
.ratio-dot--green { background: #4caf50; }
.ratio-dot--amber { background: #fbc02d; }
.ratio-dot--red { background: #f44336; }

.tile--fail .tile-metric-value { color: #f44336; }

.tile-status {
  font-size: 0.75rem;
  font-weight: 700;
  color: #f44336;
  letter-spacing: 0.05em;
}

.tile-reason {
  font-size: 0.8rem;
  color: #d29922;
  font-style: italic;
}

.kt-info-btn--tile {
  margin-left: 0.35rem;
  width: 16px;
  height: 16px;
  min-height: 16px;
  font-size: 0.65rem;
  vertical-align: middle;
}

.kt-adj-legend {
  margin: 0.15rem 0 0.25rem 0;
  padding: 0.5rem 0.65rem;
  font-size: 0.75rem;
  text-transform: none;
  letter-spacing: normal;
  font-weight: 400;
}
.kt-adj-legend sub { font-size: 0.65em; }
.kt-adj-stale { display: block; color: #d29922; font-style: italic; }

.tile-tag {
  display: inline-block;
  margin-left: 0.4rem;
  padding: 0 0.35rem;
  border-radius: 3px;
  font-size: 0.7rem;
  font-weight: 600;
  letter-spacing: 0.02em;
  background: rgba(255, 193, 7, 0.18);
  color: #b8860b;
  vertical-align: middle;
}
</style>
